import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../src/functions/useLanguage';
import { useAuth } from '../../src/context/AuthContext';
import { useStripeSubscription } from '../../src/hooks/useStripeSubscription';
import { getStarterCheckoutUrl } from '../../src/lib/stripeBilling';
import { wt } from '../i18n';
import KodaHello from '../../src/components/Custom Icons/Koda_Hello.js';
import { InstallModal } from './InstallModal';
import { getPwaInstallPrompt, clearPwaInstallPrompt } from '../../src/main';

function detectOS(): string {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Win/.test(ua)) return 'Windows';
  if (/Mac/.test(ua)) return 'macOS';
  if (/Linux/.test(ua)) return 'Linux';
  return '';
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

export function Hero() {
  const { language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [os] = useState<string>(() => detectOS());
  const [showInstallModal, setShowInstallModal] = useState(false);
  const { subscription } = useStripeSubscription(user?.id);

  // Consider free if no active, paused, past_due, trialing
  const isFree = !subscription || subscription.status === 'canceled' || subscription.status === 'incomplete';

  // Sync the module-level captured prompt into state whenever the modal opens,
  // and also update state if the event fires after mount.
  useEffect(() => {
    // Pick up any already-captured prompt (fired before this component mounted)
    setDeferredPrompt(getPwaInstallPrompt());
    const handler = (e: any) => {
      // The global listener already called e.preventDefault(); just sync state.
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const appHost = import.meta.env.VITE_APP_HOST || 'https://app.kodacare.pet';
  const postCheckoutUrl = `${appHost}/records`;

  const handleCta = () => {
    if (!isAuthenticated) {
      window.location.href = `${appHost}/register`;
      return;
    }
    
    // If authenticated but no active subscription, try to show the starter checkout
    if (isFree && user) {
      const starterUrl = getStarterCheckoutUrl({ email: user.email || '', uid: user.id, redirectUrl: postCheckoutUrl });
      if (starterUrl) {
        window.location.href = starterUrl;
        return;
      }
    }

    if (isStandalone()) {
      navigate('/records');
      return;
    }
    // Always open the modal — let the modal's button call deferredPrompt.prompt()
    // so the event isn't consumed before the user taps the branded CTA.
    setShowInstallModal(true);
  };

  const installLabel = isStandalone()
    ? t('hero.cta.openApp')
    : os
    ? `${t('hero.cta.installOn')} ${os}`
    : t('hero.cta.installApp');

  const ctaLabel = isAuthenticated ? installLabel : t('hero.cta');

  return (
    <>
      <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-white pt-16">
      <img
        src="/Decore.svg"
        alt=""
        aria-hidden="true"
        className="absolute top-0 right-0 w-[88vw] max-w-[440px] md:w-auto md:max-w-none md:h-full pointer-events-none select-none opacity-55 md:opacity-100"
      />

      {/* ── MOBILE layout ── */}
      <div className="md:hidden relative z-10 w-full px-6 flex flex-col pt-8 pb-12">

        <div className="flex items-center justify-center mb-4">
          <KodaHello className="w-full max-w-[180px] drop-shadow-2xl select-none" />
        </div>

        <div className="flex justify-center mb-4">
          <span className="inline-flex items-center gap-2 rounded-full border border-positive-green/30 bg-positive-green/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.28em] text-positive-green shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-positive-green animate-pulse" aria-hidden="true" />
            Beta
          </span>
        </div>

        {/* 1 — Full-width title */}
        <h1 className="text-[12vw] font-black text-title-primary leading-[1.05] tracking-tight mb-0 w-full uppercase text-center mx-auto">
          <span className="block">{t('hero.headline0')}</span>
          <span className="block font-light text-[0.5em]">{t('hero.headline1')} {t('hero.headline2')}</span>
          <span className="block">{t('hero.headline3')}</span>
        </h1>

        {/* 2 — Two-column: description+CTA | mascot */}
        <div className="grid grid-cols-1 gap-6 items-center mt-2">
          <div className="flex flex-col gap-4 items-center text-center">
            <p className="text-gray-500 text-sm leading-relaxed mb-4 max-w-[34ch]">
              {t('hero.body')}
            </p>
            <button
              onClick={handleCta}
              className="bg-primary-brown w-[300px] hover:bg-primary-brown/80 text-white font-bold text-sm px-8 py-3 rounded-full shadow-lg shadow-primary-brown/30 transition-all active:scale-95"
            >
              {ctaLabel}
            </button>
          </div>
        </div>

        {/* 3 — Scroll arrow */}
        <div className="flex justify-center mt-10 text-gray-400">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12l7 7 7-7" />
          </svg>
        </div>
      </div>

      {/* ── DESKTOP layout: wider left pane (60/40 split) ── */}
      <div className="hidden md:grid relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full grid-cols-5 gap-4 items-center py-20">
        <div className="col-span-3 flex flex-col items-center text-center">
          <div className="mb-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-positive-green/30 bg-positive-green/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.28em] text-positive-green shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-positive-green animate-pulse" aria-hidden="true" />
              Beta
            </span>
          </div>
          <h1 className="text-8xl lg:text-8xl font-black text-title-primary mb-0 uppercase text-center">
            <span className="block">{t('hero.headline0')}</span>
            <span className="block font-light text-[0.5em] mb-3">{t('hero.headline1')} {t('hero.headline2')}</span>
            <span className="block">{t('hero.headline3')}</span>
          </h1>
          <p className="text-gray-500 text-m leading-relaxed max-w-md mb-18 mx-auto">
            {t('hero.body')}
          </p>
          <button
            onClick={handleCta}
            className="block bg-primary-brown w-[400px] hover:bg-primary-brown/80 text-white font-bold text-base px-10 py-4 rounded-full shadow-lg shadow-primary-brown/30 transition-all hover:scale-110 active:scale-95"
          >
            {ctaLabel}
          </button>
        </div>
        <div className="col-span-2 flex items-center justify-center">
          <KodaHello className="w-auto drop-shadow-2xl" style={{ height: 'clamp(312px, 42vw, 540px)' }} />
        </div>
      </div>
      </section>
      <InstallModal
      open={showInstallModal}
      onClose={() => setShowInstallModal(false)}
      deferredPrompt={deferredPrompt}
      os={os}
      onNativeInstall={async () => {
        if (deferredPrompt) {
          deferredPrompt.prompt();
          const { outcome } = await deferredPrompt.userChoice;
          clearPwaInstallPrompt();
          setDeferredPrompt(null);
          // Close modal regardless — accepted = done, dismissed = show manual steps
          if (outcome === 'accepted') {
            setShowInstallModal(false);
          }
          // If dismissed, leave modal open so user sees manual fallback
        }
      }}
    />
    </>
  );
}